import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useClientStore } from '../../../lib/stores/clientStore';
import { useSettingsStore } from '../../../lib/stores/settingsStore';
import {
  DEFAULT_LOST_REASONS,
  buildLeadStageUpdate,
  validateLeadStageChange,
  type QuickStage,
} from '../../../lib/utils/leadStageQuickChange';

/**
 * Stato del cambio fase del Lead nel modale "Task completata" (segnalazione cac9836a).
 * Carica la pipeline del lead collegato alla task e le sue fasi attive; senza pipeline
 * (lead legacy) o senza lead collegato la sezione non compare.
 */
export function useLeadStageQuickChange(leadId: string | null, isOpen: boolean) {
  const { settings } = useSettingsStore();
  const [stages, setStages] = useState<QuickStage[]>([]);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);
  const [stageId, setStageId] = useState<string>('');
  const [lostReason, setLostReason] = useState('');

  useEffect(() => {
    setStages([]);
    setCurrentStageId(null);
    setStageId('');
    setLostReason('');
    if (!isOpen || !leadId) return;
    let cancelled = false;
    (async () => {
      const { data: client } = await supabase
        .from('clients')
        .select('pipeline_id, pipeline_stage_id')
        .eq('id', leadId)
        .maybeSingle();
      if (cancelled || !client?.pipeline_id) return;
      const { data: rows } = await supabase
        .from('lead_pipeline_stages')
        .select('id, name, is_lost, sort_order')
        .eq('pipeline_id', client.pipeline_id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      setStages((rows || []) as QuickStage[]);
      setCurrentStageId(client.pipeline_stage_id || null);
      setStageId(client.pipeline_stage_id || '');
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, isOpen]);

  const selected = stages.find((s) => s.id === stageId) || null;
  const lostReasons: string[] =
    settings?.lost_reasons && settings.lost_reasons.length > 0 ? settings.lost_reasons : DEFAULT_LOST_REASONS;

  return {
    available: !!leadId && stages.length > 0,
    stages,
    stageId,
    setStageId,
    selectedIsLost: !!selected?.is_lost,
    lostReason,
    setLostReason,
    lostReasons,
    /** Errore bloccante prima del salvataggio, o null. */
    validationError: stageId !== currentStageId ? validateLeadStageChange(selected, lostReason) : null,
    /** Scrive la nuova fase (se cambiata). Ritorna true se ha scritto. */
    commit: async (): Promise<boolean> => {
      if (!leadId) return false;
      const patch = buildLeadStageUpdate({ currentStageId, stage: selected, lostReason, nowIso: new Date().toISOString() });
      if (!patch) return false;
      const updated = await useClientStore.getState().updateClient(leadId, patch as any);
      if (!updated) throw new Error('Aggiornamento fase non riuscito');
      return true;
    },
  };
}

export type LeadStageQuickChangeState = ReturnType<typeof useLeadStageQuickChange>;
